import React, { useEffect, useState } from 'react';
import { Badge, Button, Field, Input, Modal } from '../ui';
import { Icon } from '../../icons';
import { API_URL } from '../../config';
import { createEdgeNode, deleteEdgeNode, fetchEdgeCommands, issueEdgeCommand, issueEdgePairing, setEdgeQuarantine, updateEdgeNode } from './edgeApi';
import {
  ago, commandLine, commandStatus, commandsVisible, countdown, enrolCommand, healthWarnings, livenessClass, livenessLabel, livenessTone,
  linkChips, tierLabel, uplinkLabel,
} from './edge';
import type { AuthHeadersFn, EdgeCommand, EdgeCommandKind, EdgeNode, EdgePairing } from './edgeTypes';
import type { EdgeNodesState } from './useEdgeNodes';
import './edge.css';

export interface EdgeNodesPanelProps {
  state: EdgeNodesState;
  getAuthHeaders: AuthHeadersFn;
  /** Expand this node on open — set when the panel is opened from a map marker. */
  focusNodeId?: number | null;
}

type Confirm = { kind: 'repair' | 'delete' | 'reboot'; node: EdgeNode } | null;

function fmt(v: number | undefined | null, unit: string, digits = 0): string {
  return typeof v === 'number' ? `${v.toFixed(digits)}${unit}` : '—';
}

function uptime(s: number | undefined): string {
  if (typeof s !== 'number') return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className="num">{value}</dd>
    </>
  );
}

function PairingCard({ pairing, now, onDismiss }: { pairing: EdgePairing; now: number; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const remaining = (Date.parse(pairing.expires_at) - now) / 1000;
  const expired = remaining <= 0;
  const command = enrolCommand(API_URL, pairing.pairing_code);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="edge-pairing" role="status">
      <div className="edge-pairing__head">
        <span>Pairing code for <strong>{pairing.node.name}</strong></span>
        <Button size="sm" variant="ghost" onClick={onDismiss}>Done</Button>
      </div>
      <div className={`edge-pairing__code num${expired ? ' edge-pairing__code--expired' : ''}`}>
        {pairing.pairing_code}
      </div>
      <div className="edge-pairing__expiry">
        {expired ? 'Expired — issue a new code' : `Expires in ${countdown(remaining)} · single use`}
      </div>
      <div className="edge-pairing__cmd">
        <code className="num">{command}</code>
        <Button size="sm" onClick={copy} disabled={expired}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
      <div className="edge-pairing__hint">Run this on the node. The code is shown only once.</div>
    </div>
  );
}

function PairForm({ getAuthHeaders, onPaired }: { getAuthHeaders: AuthHeadersFn; onPaired: (p: EdgePairing) => void }) {
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [errors, setErrors] = useState<{ name?: string; lat?: string; lon?: string }>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    const hasLat = lat.trim() !== '';
    const hasLon = lon.trim() !== '';
    const latitude = hasLat ? Number(lat) : undefined;
    const longitude = hasLon ? Number(lon) : undefined;
    const next: { name?: string; lat?: string; lon?: string } = {};
    if (!trimmed) next.name = 'Give the node a name';
    if (hasLon && !hasLat) next.lat = 'Give both latitude and longitude, or neither';
    else if (latitude !== undefined && (!Number.isFinite(latitude) || latitude < -90 || latitude > 90)) {
      next.lat = 'Between -90 and 90';
    }
    if (hasLat && !hasLon) next.lon = 'Give both latitude and longitude, or neither';
    else if (longitude !== undefined && (!Number.isFinite(longitude) || longitude < -180 || longitude > 180)) {
      next.lon = 'Between -180 and 180';
    }
    setErrors(next);
    if (next.name || next.lat || next.lon) return;
    setBusy(true);
    try {
      const p = await createEdgeNode(getAuthHeaders, { name: trimmed, latitude, longitude });
      setName(''); setLat(''); setLon('');
      onPaired(p);
    } catch (err) {
      // Server refusals (e.g. 409 duplicate name) are about the node as a whole; show them on Name.
      setErrors({ name: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="edge-form" onSubmit={submit}>
      <Field label="Name" error={errors.name}>
        {p => <Input {...p} value={name} maxLength={100} onChange={e => setName(e.target.value)} placeholder="e.g. ridge-pi-01" />}
      </Field>
      <div className="edge-form__row">
        <Field label="Latitude" hint="Optional" error={errors.lat}>
          {p => <Input {...p} value={lat} inputMode="decimal" onChange={e => setLat(e.target.value)} />}
        </Field>
        <Field label="Longitude" hint="Optional" error={errors.lon}>
          {p => <Input {...p} value={lon} inputMode="decimal" onChange={e => setLon(e.target.value)} />}
        </Field>
      </div>
      <Button type="submit" variant="primary" size="sm" loading={busy}>Issue pairing code</Button>
    </form>
  );
}

function NodeDetails({ node }: { node: EdgeNode }) {
  const m = node.manifest || {};
  const h = node.health || {};
  const cams = m.cameras && m.cameras.length ? m.cameras.map(c => `${c.id} (${c.kind})`).join(', ') : '—';
  return (
    <div className="edge-details">
      <dl>
        <Detail label="Hostname" value={m.hostname || '—'} />
        <Detail label="Model" value={m.model || '—'} />
        <Detail label="OS" value={m.os || '—'} />
        <Detail label="Python" value={m.python || '—'} />
        <Detail label="Agent" value={h.agent_version || m.agent_version || '—'} />
        <Detail label="Accelerators" value={m.accelerators && m.accelerators.length ? m.accelerators.join(', ') : 'none'} />
        <Detail label="Cameras" value={cams} />
      </dl>
      <dl>
        <Detail label="CPU" value={fmt(h.cpu_pct, '%')} />
        <Detail label="Memory" value={fmt(h.mem_pct, '%')} />
        <Detail label="Temp" value={fmt(h.temp_c, '°C')} />
        <Detail label="Disk free" value={fmt(h.disk_free_mb, ' MB')} />
        <Detail label="Uptime" value={uptime(h.uptime_s)} />
        <Detail label="Uplink" value={`${uplinkLabel(h.uplink)}${h.uplink_iface ? ` (${h.uplink_iface})` : ''}`} />
        <Detail label="Link gaps" value={typeof h.link_gaps === 'number' ? `${h.link_gaps}${typeof h.longest_gap_s === 'number' ? `, longest ${Math.round(h.longest_gap_s)}s` : ''}` : '—'} />
        <Detail label="Clock" value={`${h.clock_source || '—'}${typeof h.clock_skew_s === 'number' ? `, skew ${h.clock_skew_s.toFixed(1)}s` : ''}`} />
        <Detail
          label="Position"
          value={node.latitude != null && node.longitude != null
            ? `${node.latitude.toFixed(5)}, ${node.longitude.toFixed(5)} (${node.position_source || 'unknown'})`
            : 'not set'}
        />
      </dl>
    </div>
  );
}

function LinkChips({ node }: { node: EdgeNode }) {
  return (
    <span className="edge-links" aria-label="Links">
      {linkChips(node).map(c => (
        <Badge key={c.label} tone={c.tone} dot className={`edge-link-chip ${livenessClass(c.liveness)}`} title={`${c.label}: ${livenessLabel(c.liveness)}`}>
          {c.label}
        </Badge>
      ))}
    </span>
  );
}

function CommandBar({ node, busy, onIssue }: { node: EdgeNode; busy: boolean; onIssue: (kind: EdgeCommandKind, arg?: string) => void }) {
  const services = node.manifest?.lora_services ?? [];
  const [picked, setPicked] = useState('');
  const service = services.includes(picked) ? picked : (services[0] ?? '');
  return (
    <div className="edge-cmds">
      <Button size="sm" disabled={busy} onClick={() => onIssue('ping')}>Ping</Button>
      <Button size="sm" disabled={busy} onClick={() => onIssue('restart-uplink')}>Restart uplink</Button>
      <span className="edge-cmds__service">
        <select
          className="edge-select"
          value={service}
          aria-label="Service to restart"
          disabled={busy || services.length === 0}
          onChange={e => setPicked(e.target.value)}
        >
          {services.length === 0 && <option value="">no services listed by the node</option>}
          {services.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button size="sm" disabled={busy || !service} onClick={() => onIssue('restart-service', service)}>Restart service</Button>
      </span>
      <Button size="sm" variant="danger" disabled={busy} onClick={() => onIssue('reboot')}>Reboot</Button>
    </div>
  );
}

function CommandHistory({ commands, now }: { commands: EdgeCommand[] | undefined; now: number }) {
  if (!commands) return <p className="edge-empty">Loading commands…</p>;
  if (commands.length === 0) return <p className="edge-empty">No commands sent over LoRa yet.</p>;
  return (
    <ul className="edge-cmd-history">
      {commands.map(c => {
        const s = commandStatus(c);
        return (
          <li key={c.id}>
            <span className="edge-cmd-history__what">{commandLine(c)}</span>
            <Badge tone={s.tone}>{s.text}</Badge>
            <span className="edge-cmd-history__when num">{ago(c.issued_at, now)}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default function EdgeNodesPanel({ state, getAuthHeaders, focusNodeId }: EdgeNodesPanelProps) {
  const { data, error, errorStatus, refresh } = state;
  const [now, setNow] = useState(() => Date.now());
  const [expanded, setExpanded] = useState<number | null>(focusNodeId ?? null);
  const [showForm, setShowForm] = useState(false);
  const [pairing, setPairing] = useState<EdgePairing | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [commands, setCommands] = useState<Record<number, EdgeCommand[]>>({});
  const loraEnabled = !!data?.lora_enabled;

  const loadCommands = async (nodeId: number) => {
    try {
      const r = await fetchEdgeCommands(getAuthHeaders, nodeId);
      setCommands(prev => ({ ...prev, [nodeId]: r.commands }));
    } catch {
      // the panel's own error banner covers the node list; a history miss is not fatal
    }
  };

  // The expanded node's command history follows the same 10 s rhythm as the list.
  useEffect(() => {
    if (expanded == null || !loraEnabled) return undefined;
    loadCommands(expanded);
    const id = window.setInterval(() => loadCommands(expanded), 10_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, loraEnabled]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (focusNodeId != null) setExpanded(focusNodeId);
  }, [focusNodeId]);

  // Reopening the panel retries a poll that a 403 ('no group') stopped.
  useEffect(() => {
    if (state.errorStatus === 403) state.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (nodeId: number, fn: () => Promise<unknown>) => {
    setBusyId(nodeId);
    setActionError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const issue = (node: EdgeNode, kind: EdgeCommandKind, arg?: string) => {
    run(node.id, async () => {
      await issueEdgeCommand(getAuthHeaders, node.id, kind, arg);
      await loadCommands(node.id);
    });
  };

  const onConfirm = () => {
    if (!confirm) return;
    const { kind, node } = confirm;
    setConfirm(null);
    if (kind === 'repair') {
      run(node.id, async () => setPairing(await issueEdgePairing(getAuthHeaders, node.id)));
    } else if (kind === 'reboot') {
      issue(node, 'reboot');
    } else {
      run(node.id, () => deleteEdgeNode(getAuthHeaders, node.id));
    }
  };

  if (errorStatus === 403) {
    return <div className="edge-panel"><p className="edge-empty">Join a group to see and pair edge nodes.</p></div>;
  }

  const nodes = data?.nodes ?? [];
  const canManage = !!data?.can_manage;

  return (
    <div className="edge-panel">
      {error && <div className="edge-error" role="alert">Could not refresh edge nodes: {error}</div>}
      {actionError && <div className="edge-error" role="alert">{actionError}</div>}

      {canManage && (
        <div className="edge-toolbar">
          <Button size="sm" variant={showForm ? 'secondary' : 'primary'} onClick={() => setShowForm(v => !v)}>
            {showForm ? 'Cancel' : 'Pair new node'}
          </Button>
          <Button size="sm" variant="ghost" icon={<Icon name="refresh" />} onClick={() => { refresh(); }}>Refresh</Button>
        </div>
      )}
      {canManage && showForm && (
        <PairForm getAuthHeaders={getAuthHeaders} onPaired={p => { setPairing(p); setShowForm(false); refresh(); }} />
      )}
      {pairing && <PairingCard pairing={pairing} now={now} onDismiss={() => setPairing(null)} />}

      {!data && !error && <p className="edge-empty">Loading…</p>}
      {data && nodes.length === 0 && (
        <p className="edge-empty">No edge nodes in this group yet.{canManage ? ' Pair a Pi or Jetson to add one.' : ''}</p>
      )}

      <ul className="edge-list">
        {nodes.map(node => {
          const warnings = healthWarnings(node);
          const open = expanded === node.id;
          return (
            <li key={node.id} className={`edge-row${open ? ' edge-row--open' : ''}`}>
              <button
                type="button"
                className="edge-row__main"
                aria-expanded={open}
                onClick={() => setExpanded(open ? null : node.id)}
              >
                <span className="edge-row__name" title={node.name}>
                  <Icon name={open ? 'chev-down' : 'chev-right'} />
                  <span className="edge-row__name-text">{node.name}</span>
                </span>
                <Badge tone={livenessTone(node.liveness)} dot className={livenessClass(node.liveness)}>
                  {livenessLabel(node.liveness)}
                </Badge>
                <LinkChips node={node} />
                <span className="edge-row__meta">
                  {tierLabel(node.tier)} · {uplinkLabel(node.health?.uplink)} ·{' '}
                  <span className="num">{node.pairing_pending && !node.paired ? 'awaiting enrolment' : ago(node.last_seen, now)}</span>
                </span>
              </button>
              {warnings.length > 0 && (
                <div className="edge-row__warnings">
                  {warnings.map(w => (
                    <Badge key={w} tone={w === 'Quarantined' ? 'danger' : 'warning'}>{w}</Badge>
                  ))}
                </div>
              )}
              {open && (
                <>
                  <NodeDetails node={node} />
                  {loraEnabled && node.last_seen_lora && (
                    <>
                      {canManage && commandsVisible(node, loraEnabled) && (
                        <CommandBar
                          node={node}
                          busy={busyId === node.id}
                          onIssue={(kind, arg) => (kind === 'reboot' ? setConfirm({ kind: 'reboot', node }) : issue(node, kind, arg))}
                        />
                      )}
                      <CommandHistory commands={commands[node.id]} now={now} />
                    </>
                  )}
                  {canManage && (
                    <div className="edge-row__actions">
                      <Button size="sm" disabled={busyId === node.id} onClick={() => setConfirm({ kind: 'repair', node })}>
                        Re-pair
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busyId === node.id}
                        onClick={() => run(node.id, () => setEdgeQuarantine(getAuthHeaders, node.id, !node.quarantined))}
                      >
                        {node.quarantined ? 'Release' : 'Quarantine'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busyId === node.id}
                        onClick={() => run(node.id, () => updateEdgeNode(getAuthHeaders, node.id, { lora_gateway: !node.lora_gateway }))}
                      >
                        {node.lora_gateway ? 'Unmark gateway' : 'Mark as gateway'}
                      </Button>
                      <Button size="sm" variant="danger" className="edge-row__delete" disabled={busyId === node.id} onClick={() => setConfirm({ kind: 'delete', node })}>
                        Delete
                      </Button>
                    </div>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {confirm && (
        <Modal
          title={confirm.kind === 'repair' ? `Re-pair ${confirm.node.name}?` : confirm.kind === 'reboot' ? `Reboot ${confirm.node.name} over LoRa?` : `Delete ${confirm.node.name}?`}
          onClose={() => setConfirm(null)}
          width={380}
          footer={
            <>
              <Button onClick={() => setConfirm(null)}>Cancel</Button>
              <Button variant="danger-solid" onClick={onConfirm}>
                {confirm.kind === 'repair' ? 'Revoke and re-pair' : confirm.kind === 'reboot' ? 'Send reboot' : 'Delete node'}
              </Button>
            </>
          }
        >
          {confirm.kind === 'repair'
            ? <p>Re-pair revokes this node&apos;s credentials now. It stops reporting until it is enrolled again with the new code.</p>
            : confirm.kind === 'reboot'
              ? <p>The node acknowledges first and then reboots. Its cameras and relays are down until it is back, and the command cannot be recalled once radioed.</p>
              : <p>This removes the node and its credentials from the group. It cannot be undone.</p>}
        </Modal>
      )}
    </div>
  );
}
